import { 
  doc, 
  updateDoc, 
  getDoc, 
  setDoc, 
  deleteDoc 
} from 'firebase/firestore';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import { buildFirestorePath, buildStoragePath } from './pathUtils';
import logger from './logger';

/**
 * Share a file with family members
 * @param {Object} params
 * @param {string} params.fileId - ID of the file to share
 * @param {string} params.userId - Current user's ID
 * @param {string} params.familyCode - Family code
 * @param {string} params.collectionType - Type of collection (photos/documents)
 * @returns {Promise<boolean>} Success status
 */
export const shareWithFamily = async ({ fileId, userId, familyCode, collectionType }) => {
  try {
    // Get the original file from personal storage
    const personalPath = buildFirestorePath({
      profileId: familyCode,
      userId,
      collectionType,
      storageArea: 'personal'
    });
    
    const fileDoc = await getDoc(doc(db, personalPath, fileId));
    if (!fileDoc.exists()) {
      throw new Error('File not found in personal storage');
    }

    const fileData = fileDoc.data();

    // Create the shared file entry in family storage
    const familyPath = buildFirestorePath({
      profileId: familyCode,
      collectionType,
      storageArea: 'family'
    });

    await setDoc(doc(db, familyPath, fileId), {
      ...fileData,
      originalOwner: userId,
      sharedAt: new Date(),
      storageArea: 'family',
      isShared: true,
      familyCode
    });

    // Update the original file to mark it as shared
    await updateDoc(doc(db, personalPath, fileId), {
      isShared: true,
      sharedAt: new Date()
    });

    return true;
  } catch (error) {
    logger.error('Error sharing file with family:', error);
    return false;
  }
};

/**
 * Move a file to the family vault
 * @param {Object} params
 * @param {string} params.fileId - ID of the file to move
 * @param {string} params.userId - Current user's ID
 * @param {string} params.familyCode - Family code
 * @param {string} params.collectionType - Type of collection (photos/documents)
 * @param {boolean} params.isConfidential - Whether the file is confidential
 * @returns {Promise<boolean>} Success status
 */
export const moveToVault = async ({ fileId, userId, familyCode, collectionType, isConfidential }) => {
  try {
    // Get the original file location (could be personal or family)
    const sourcePath = buildFirestorePath({
      profileId: familyCode,
      userId,
      collectionType,
      storageArea: isConfidential ? 'personal' : 'family'
    });

    const fileDoc = await getDoc(doc(db, sourcePath, fileId));
    if (!fileDoc.exists()) {
      throw new Error('Source file not found');
    }

    const fileData = fileDoc.data();

    // Create the vault file entry
    const vaultPath = buildFirestorePath({
      profileId: familyCode,
      collectionType,
      storageArea: 'vault'
    });

    await setDoc(doc(db, vaultPath, fileId), {
      ...fileData,
      movedFromArea: fileData.storageArea,
      movedToVaultAt: new Date(),
      storageArea: 'vault',
      isConfidential,
      requiresPermission: true
    });

    // Delete the original file
    await deleteDoc(doc(db, sourcePath, fileId));

    return true;
  } catch (error) {
    logger.error('Error moving file to vault:', error);
    return false;
  }
};

/**
 * Copy a file to a different folder
 * @param {Object} params
 * @param {string} params.fileId - ID of the file to copy
 * @param {string} params.userId - Current user's ID
 * @param {string} params.familyCode - Family code
 * @param {string} params.collectionType - Type of collection (photos/documents)
 * @param {string} params.sourceStorageArea - Source storage area (personal/family/vault)
 * @param {string} params.targetStorageArea - Target storage area (personal/family/vault)
 * @param {string} params.targetPath - Target folder path
 * @returns {Promise<boolean>} Success status
 */
/**
 * Move a file from vault back to personal storage
 * @param {Object} params
 * @param {string} params.fileId - ID of the file to move
 * @param {string} params.userId - Current user's ID
 * @param {string} params.familyCode - Family code
 * @param {string} params.collectionType - Type of collection (photos/documents)
 * @returns {Promise<boolean>} Success status
 */
export const moveFromVault = async ({ fileId, userId, familyCode, collectionType }) => {
  try {
    // Get the file from vault
    const vaultPath = buildFirestorePath({
      profileId: familyCode,
      collectionType,
      storageArea: 'vault'
    });

    const fileDoc = await getDoc(doc(db, vaultPath, fileId));
    if (!fileDoc.exists()) {
      throw new Error('File not found in vault');
    }

    const fileData = fileDoc.data();

    // Create the personal file entry
    const personalPath = buildFirestorePath({
      profileId: familyCode,
      userId,
      collectionType,
      storageArea: 'personal'
    });

    await setDoc(doc(db, personalPath, fileId), {
      ...fileData,
      movedFromVaultAt: new Date(),
      storageArea: 'personal',
      isConfidential: false,
      requiresPermission: false
    });

    // Delete the vault file
    await deleteDoc(doc(db, vaultPath, fileId));

    return true;
  } catch (error) {
    logger.error('Error moving file from vault:', error);
    return false;
  }
};

export const copyFile = async ({
  fileId,
  userId,
  familyCode,
  collectionType,
  sourceStorageArea,
  targetStorageArea,
  targetPath
}) => {
  try {
    // Get the source file
    const sourcePath = buildFirestorePath({
      profileId: familyCode,
      userId: sourceStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: sourceStorageArea
    });

    const fileDoc = await getDoc(doc(db, sourcePath, fileId));
    if (!fileDoc.exists()) {
      throw new Error('Source file not found');
    }

    const fileData = fileDoc.data();
    const newFileId = uuidv4(); // Generate new ID for the copy

    // Create storage reference for the new copy
    const storageSourcePath = buildStoragePath({
      profileId: familyCode,
      userId: sourceStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: sourceStorageArea,
      fileId
    });

    const storageTargetPath = buildStoragePath({
      profileId: familyCode,
      userId: targetStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: targetStorageArea,
      fileId: newFileId
    });

    // Copy the file in Storage
    const sourceRef = ref(storage, storageSourcePath);
    const targetRef = ref(storage, storageTargetPath);

    // Get the download URL of the source file
    const sourceUrl = await getDownloadURL(sourceRef);

    // Download and re-upload the file
    const response = await fetch(sourceUrl);
    const blob = await response.blob();
    await uploadBytes(targetRef, blob);
    const newDownloadUrl = await getDownloadURL(targetRef);

    // Create the target file entry in Firestore
    const destFirestorePath = buildFirestorePath({
      profileId: familyCode,
      userId: targetStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: targetStorageArea
    });

    await setDoc(doc(db, destFirestorePath, newFileId), {
      ...fileData,
      id: newFileId,
      url: newDownloadUrl,
      path: targetPath || '/',
      storageArea: targetStorageArea,
      copiedFrom: {
        id: fileId,
        storageArea: sourceStorageArea,
        userId: fileData.userId
      },
      copiedAt: new Date(),
      copiedBy: userId,
      lastModified: new Date(),
      lastModifiedBy: userId
    });

    return true;
  } catch (error) {
    logger.error('Error copying file:', error);
    return false;
  }
};

/**
 * Move a file between any storage areas (personal/family/vault)
 * Copies metadata + file to target, then deletes source
 */
export const moveFile = async ({
  fileId,
  userId,
  familyCode,
  collectionType,
  sourceStorageArea,
  targetStorageArea,
  targetPath
}) => {
  try {
    // Get the source file
    const sourcePath = buildFirestorePath({
      profileId: familyCode,
      userId: sourceStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: sourceStorageArea
    });

    const fileDocSnap = await getDoc(doc(db, sourcePath, fileId));
    if (!fileDocSnap.exists()) {
      throw new Error('Source file not found');
    }

    const fileData = fileDocSnap.data();

    // Build target Firestore path
    const destFirestorePath = buildFirestorePath({
      profileId: familyCode,
      userId: targetStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: targetStorageArea
    });

    // Handle Storage file: download from source and upload to target
    const storageSourcePath = buildStoragePath({
      profileId: familyCode,
      userId: sourceStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: sourceStorageArea,
      fileId
    });

    const storageTargetPath = buildStoragePath({
      profileId: familyCode,
      userId: targetStorageArea === 'personal' ? userId : null,
      collectionType,
      storageArea: targetStorageArea,
      fileId
    });

    let newDownloadUrl = fileData.url;

    // Only re-upload if storage paths differ
    if (storageSourcePath !== storageTargetPath) {
      try {
        const sourceRef = ref(storage, storageSourcePath);
        const targetRef = ref(storage, storageTargetPath);

        const sourceUrl = await getDownloadURL(sourceRef);
        const response = await fetch(sourceUrl);
        const blob = await response.blob();
        await uploadBytes(targetRef, blob);
        newDownloadUrl = await getDownloadURL(targetRef);
      } catch (storageError) {
        // If storage copy fails (e.g., file doesn't exist in Storage), keep original URL
        logger.warn('Storage move failed, keeping original URL:', storageError);
      }
    }

    // Create in target
    await setDoc(doc(db, destFirestorePath, fileId), {
      ...fileData,
      url: newDownloadUrl,
      path: targetPath || '/',
      storageArea: targetStorageArea,
      movedFrom: sourceStorageArea,
      movedAt: new Date(),
      movedBy: userId,
      lastModified: new Date(),
      lastModifiedBy: userId,
      // Add vault-specific fields
      ...(targetStorageArea === 'vault' ? { requiresPermission: true, isConfidential: true } : {}),
      ...(sourceStorageArea === 'vault' ? { requiresPermission: false, isConfidential: false } : {})
    });

    // Delete from source
    await deleteDoc(doc(db, sourcePath, fileId));

    // Try to delete source storage file (non-critical if fails)
    if (storageSourcePath !== storageTargetPath) {
      try {
        const { deleteObject } = await import('firebase/storage');
        const sourceRef = ref(storage, storageSourcePath);
        await deleteObject(sourceRef);
      } catch {
        // Source storage file cleanup is best-effort
      }
    }

    return true;
  } catch (error) {
    logger.error('Error moving file:', error);
    return false;
  }
}; 